"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { Badge } from "@/components/ui/badge";
import { Atom, Sparkles, Orbit, Compass, ArrowDown } from "lucide-react";
import "@/components/cosmic/cosmic.css";
export default function UniverseIntro() {
  return <section id="what-if-intro" data-nav="dark" className="relative overflow-hidden w-full max-w-full pt-24 sm:pt-36 pb-14 sm:pb-24">
      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center min-w-0">
        {/* Law Dial / Coordinate Graphic Header Element */}
        <div className="flex items-center justify-center mb-6">
          <div className="relative inline-flex items-center justify-center size-16 sm:size-24 rounded-full border border-primary/40 bg-card/60 backdrop-blur-md shadow-[0_0_24px_var(--glow-primary)]">
            {/* Concentric rotating dial ring */}
            <div className="absolute inset-1.5 rounded-full border border-dashed border-primary/50 ql-spin-slow pointer-events-none" aria-hidden="true" />
            {/* Inner coordinate hub */}
            <div className="size-9 sm:size-12 rounded-full bg-primary/20 border border-primary flex items-center justify-center text-primary">
              <Orbit className="size-4 sm:size-6 text-primary animate-pulse" />
            </div>
            {/* Compass calibration ticks */}
            <span className="absolute top-1 text-[8px] font-mono text-primary/70">N</span>
            <span className="absolute bottom-1 text-[8px] font-mono text-primary/70">S</span>
            <span className="absolute left-1 text-[8px] font-mono text-primary/70">W</span>
            <span className="absolute right-1 text-[8px] font-mono text-primary/70">E</span>
          </div>
        </div>

        {/* Section Heading as H1 */}
        <SectionHeading as="h1" eyebrow="THOUGHT EXPERIMENT PROTOCOL // LAW DIAL v3" title="What-If Universe" description="Our universe appears remarkably fine-tuned for the emergence of stars, chemistry, and life. But what happens if you rewrite the foundational constants that govern spacetime?" align="center" className="!mb-6 break-words" />

        {/* Body Explanatory Text */}
        <Reveal delay={150}>
          <div className="max-w-2xl mx-auto space-y-4 mb-8">
            <p className="text-sm sm:text-base lg:text-lg text-foreground/90 font-normal leading-relaxed break-words">
              In this guided thought experiment, you will select hypothetical
              cosmic conditions—altering gravitational strength, spatial
              dimensionality, and the speed of light. Using established
              astrophysical models, we project what kind of cosmos would
              assemble, whether atoms could hold together, and if macroscopic
              complexity could ever arise.
            </p>
          </div>
        </Reveal>

        {/* Clear Legend of Two Badge Types used throughout the entire page */}
        <Reveal delay={250}>
          <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 p-3 sm:p-5 rounded-2xl ql-glass-strong border border-border/80 max-w-full sm:max-w-2xl mx-auto shadow-lg">
            <span className="text-[11px] sm:text-xs font-mono uppercase tracking-widest text-muted-foreground mr-1 text-center">
              Methodology Legend:
            </span>

            {/* Cyan: Established Science */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Badge className="bg-primary/20 text-primary border-primary/50 font-mono text-[11px] sm:text-xs uppercase tracking-wider py-1 px-3 shadow-[0_0_10px_var(--glow-primary)]">
                <Atom className="size-3.5 mr-1.5 shrink-0" />
                <span>Established Science</span>
              </Badge>
              <span className="text-xs text-muted-foreground text-left hidden md:inline">
                Mathematical physics & empirical bounds
              </span>
            </div>

            {/* Violet/Gold: Speculation */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/50 font-mono text-[11px] sm:text-xs uppercase tracking-wider py-1 px-3 shadow-[0_0_10px_var(--glow-violet)]">
                <Sparkles className="size-3.5 mr-1.5 text-purple-400 shrink-0" />
                <span>Speculation</span>
              </Badge>
              <span className="text-xs text-muted-foreground text-left hidden md:inline">
                Theoretical thought experiment
              </span>
            </div>
          </div>
        </Reveal>

        {/* Quick jump anchor to interactive sections */}
        <div className="mt-8 flex justify-center">
          <Link to="#law-rewriter" className="ql-btn-ghost text-xs group min-h-[44px] inline-flex items-center gap-2 px-4 py-2">
            <span>Explore Law Rewriter</span>
            <ArrowDown className="size-3.5 text-primary group-hover:translate-y-0.5 transition-transform shrink-0" />
          </Link>
        </div>
      </div>
    </section>;
}